import {
    ECAPITAL_PROTOCOL_VERSION,
    ECAPITAL_SESSION_CHECK_TIMEOUT_MS,
    type EcapitalCheckSessionResponse,
    type EcapitalSessionState,
} from '@numeo/types-dispatcher-ui'
import axios from 'axios'
import { EXTENSION_ID } from './constants'
import { isExtensionAvailable } from './extension'

/**
 * Whether the dispatcher's eCapital login is still alive.
 *
 * The server cannot know — the login is a cookie in the dispatcher's browser — so "Connected" used to stay on
 * long after the portal had logged them out (the portal answers `401 {"error":"Invalid token"}`). The extension
 * can see it, from the portal tab, with one GET; this asks it and tells the server what it found, so the Settings
 * page and Money Operations (both read /factoring/status) agree.
 */
const CHECK_TIMEOUT_MS = ECAPITAL_SESSION_CHECK_TIMEOUT_MS
// How long each answer is remembered. A missing portal tab ("unknown") is remembered because it is the slow answer: without
// it every status read waits out CHECK_TIMEOUT_MS again, and /factoring/status is read by Settings, Invoices and Money
// Operations alike. An "expired" answer is NOT remembered: it comes back fast, and remembering it kept the Invoices page
// showing "expired" for a while after the dispatcher reconnected from its Authorize button (nothing clears this cache
// there), which is the one moment the answer is guaranteed to have changed.
const ANSWER_CACHE_MS: Record<EcapitalSessionState, number> = { valid: 60_000, expired: 0, unknown: 30_000 }

/** What a check found, and for which eCapital client when the extension said: a project's two connections can be different clients. */
interface SessionAnswer {
    state: EcapitalSessionState
    clientNo?: string
}

let lastAnswer: (SessionAnswer & { at: number }) | null = null
let pendingCheck: Promise<SessionAnswer> | null = null

export function clearEcapitalSessionCache(): void {
    lastAnswer = null
    pendingCheck = null
}

/** One read-only question to the extension. Null: no extension, no answer in time, or a channel error. */
function askExtension(): Promise<EcapitalCheckSessionResponse | null> {
    if (!isExtensionAvailable()) return Promise.resolve(null)

    return new Promise((resolve) => {
        let settled = false
        const finish = (answer: EcapitalCheckSessionResponse | null) => {
            if (settled) return
            settled = true
            clearTimeout(timer)
            resolve(answer)
        }
        const timer = setTimeout(() => finish(null), CHECK_TIMEOUT_MS)
        try {
            chrome.runtime.sendMessage(EXTENSION_ID, { type: 'ECAPITAL_CHECK_SESSION' }, (response?: EcapitalCheckSessionResponse) => {
                finish(chrome.runtime.lastError || !response ? null : response)
            })
        } catch {
            finish(null)
        }
    })
}

async function askSession(): Promise<SessionAnswer> {
    const answer = await askExtension()
    return answer?.status === 'success' ? { state: answer.session ?? 'unknown', clientNo: answer.clientNo } : { state: 'unknown' }
}

/** Asks the extension (read-only). `unknown` — no extension, no portal tab, no answer — is never "expired". */
export async function checkEcapitalSession(): Promise<EcapitalSessionState> {
    return (await askSession()).state
}

type EcapitalExtensionCheck = { current: true } | { current: false; message: string }

/**
 * Whether the installed extension speaks this release's eCapital protocol. The admin and the extension ship separately, and a
 * mismatch either misses a record (new server, old admin) or silently skips a lock (new admin, old extension), so a submission
 * does not start through an extension that has not reported a version at least as new as `ECAPITAL_PROTOCOL_VERSION`.
 * A build from before the version existed does not report one at all.
 */
export async function checkEcapitalExtensionIsCurrent(): Promise<EcapitalExtensionCheck> {
    if (!isExtensionAvailable()) return { current: false, message: 'Numeo Chrome Extension is required to submit to eCapital.' }
    const answer = await askExtension()
    if (typeof answer?.protocolVersion === 'number' && answer.protocolVersion >= ECAPITAL_PROTOCOL_VERSION) return { current: true }
    return {
        current: false,
        message:
            'The Numeo Chrome Extension is out of date or not responding. Update it to the latest version (chrome://extensions → Update), reload this page and submit again.',
    }
}

/**
 * Best-effort: a failed report only means the next status read tries again. `clientNo` is the eCapital client the finding is
 * about: only that client's connections change. Without it, both of the project's do.
 */
export async function reportEcapitalSessionValidity(valid: boolean, clientNo?: string): Promise<void> {
    try {
        await axios.post('/factoring/ecapital/session-status', { valid, ...(clientNo ? { clientNo } : {}) })
    } catch (error) {
        console.error('[FactoringService] Failed to record the eCapital session state:', error)
    }
}

/** The remembered answer while it is fresh; otherwise one portal check, shared by every caller waiting on it. */
function resolveSession(): Promise<SessionAnswer> {
    if (lastAnswer && Date.now() - lastAnswer.at < ANSWER_CACHE_MS[lastAnswer.state]) return Promise.resolve(lastAnswer)
    if (pendingCheck) return pendingCheck

    const check: Promise<SessionAnswer> = askSession()
        .then((answer) => {
            // A cache clear while the check was in flight means its answer may already be stale.
            if (pendingCheck === check) lastAnswer = { ...answer, at: Date.now() }
            return answer
        })
        .finally(() => {
            if (pendingCheck === check) pendingCheck = null
        })
    pendingCheck = check
    return check
}

interface StatusWithEcapital {
    ecapital?: { connected?: boolean; isValid?: boolean; clientNo?: string }
}

/**
 * Corrects a /factoring/status answer with what this browser's portal tab says. Leaves it untouched when nothing could be
 * verified, and when eCapital is not connected at all.
 */
export async function reconcileEcapitalSession<T extends StatusWithEcapital>(status: T): Promise<T> {
    const ecapital = status.ecapital
    if (!ecapital?.connected) return status

    const { state: session, clientNo } = await resolveSession()
    // A check of another eCapital client says nothing about this connection's login.
    if (clientNo && ecapital.clientNo && clientNo !== ecapital.clientNo) return status
    if (session === 'valid' && !ecapital.isValid) {
        await reportEcapitalSessionValidity(true, clientNo)
        return { ...status, ecapital: { ...ecapital, isValid: true } }
    }
    // What THIS browser's portal tab says is true for this browser only: the connection is the project's, and another
    // dispatcher's tab may well be logged in. So an expired tab changes what this dispatcher sees, but is never written to
    // the project — that is left to a submission that actually got the 401 (see reportSubmitFailure).
    if (session === 'expired' && ecapital.isValid) return { ...status, ecapital: { ...ecapital, isValid: false } }
    return status
}
